import { useCallback, useState } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { v5 as uuidv5 } from "uuid";

import { formatTerminationReason } from "src/agents/base/formatTerminationReason";
import type { ToolCallWithResult } from "src/agents/base/ToolCallDisplay";
import { useAgentExecutionClient } from "src/agents/queries/execution";
import { useCaseCopilotInvoke } from "src/cases/copilot/useCaseCopilotInvoke";
import { caseHintsKeys } from "src/cases/queries/hints";
import type {
  SessionMessagesResponse,
  SessionMessagesResponseMessagesInner,
  ToolCallContentBlock,
  ToolMessage,
} from "src/clients/agent-management";
import { toastActions } from "src/design-system/Toast/utils";
import type { DashboardPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export type CopilotChatMessage =
  | { role: "user"; content: string }
  | { role: "assistant"; content: string }
  | { role: "tool-group"; toolCallsWithResults: ToolCallWithResult[] };

const extractMessages = (
  sessionMessages: SessionMessagesResponseMessagesInner[],
): CopilotChatMessage[] => {
  // Build lookup: tool_call_id → ToolMessage
  const toolResultMap = new Map<string, ToolMessage>();
  for (const msg of sessionMessages) {
    if (msg.type === "tool") {
      toolResultMap.set(msg.tool_call_id, msg);
    }
  }

  const result: CopilotChatMessage[] = [];

  for (const msg of sessionMessages) {
    if (msg.type === "human") {
      const text =
        msg.invocation_message ??
        (typeof msg.content === "string" ? msg.content : "");
      if (text) {
        result.push({ role: "user", content: text });
      }
    } else if (msg.type === "ai") {
      const blocks = msg.content_blocks ?? [];

      const textContent = blocks
        .filter((b) => b.type === "text")
        .map((b) => b.text)
        .filter((text): text is string => text !== undefined && text !== null)
        .join("\n\n");

      const toolCalls = blocks.filter(
        (b) => b.type === "tool_call",
      ) as ToolCallContentBlock[];

      if (toolCalls.length > 0) {
        result.push({
          role: "tool-group",
          toolCallsWithResults: toolCalls.map((tc) => ({
            toolCall: tc,
            toolResult: toolResultMap.get(tc.id) ?? null,
          })),
        });
      }
      if (textContent) {
        result.push({ role: "assistant", content: textContent });
      }
    }
  }

  // Keep BE order: newest first. The component handles rendering order.
  return result;
};

/**
 * Checks whether any newly added messages (since `lastKnownId`) are tool
 * messages.  Messages are expected in **newest-first** order.
 */
export const hasNewToolMessages = (
  messages: SessionMessagesResponseMessagesInner[],
  lastKnownId: string | null | undefined,
): boolean => {
  const boundaryIndex = lastKnownId
    ? messages.findIndex((m) => m.id === lastKnownId)
    : -1;
  const newMessages =
    boundaryIndex === -1 ? messages : messages.slice(0, boundaryIndex);
  return newMessages.some((m) => m.type === "tool");
};

const TERMINAL_STATUSES = new Set(["success", "failed", "cancelled"]);

const SESSION_CACHE_KEY_PREFIX = "agent-execution";

const SESSION_NAMESPACE = "a3f2b8c1-7d4e-4f9a-b6e5-1c8d9e0f2a3b";

const getSessionId = (
  userId: string | undefined,
  caseId: string,
  agentVersionId: string | undefined,
): string | null =>
  userId
    ? uuidv5(
        `${caseId}:${userId}:${agentVersionId ?? "latest"}`,
        SESSION_NAMESPACE,
      )
    : null;

type UseCaseCopilotSessionParams = {
  agentId: string | undefined;
  agentVersionId: string | undefined;
  caseId: string;
  userId: string | undefined;
  environment: string;
};

export const useCaseCopilotSession = ({
  agentId,
  agentVersionId,
  caseId,
  userId,
  environment,
}: UseCaseCopilotSessionParams) => {
  const sessionId = getSessionId(userId, caseId, agentVersionId);

  const [hasStarted, setHasStarted] = useState(false);

  const client = useAgentExecutionClient();
  const queryClient = useQueryClient();
  const { wsId } = useParamsDecode<DashboardPageParams>();

  const messagesQueryKey = [
    SESSION_CACHE_KEY_PREFIX,
    "sessions",
    sessionId,
    "messages",
  ] as const;

  // Define mutation before query so we can use invokeMutation.isPending
  // in refetchInterval to pause polling during optimistic updates.
  const invokeMutation = useCaseCopilotInvoke({
    agentId,
    agentVersionId,
    caseId,
    sessionId,
    environment,
    messagesQueryKey,
    onInvokeStarted: () => setHasStarted(true),
  });

  // Poll messages via /copilots/management/api/v0/sessions/{session_id}/messages
  // The response also includes session status, so no separate session query is needed.
  const messagesQuery = useQuery({
    queryKey: messagesQueryKey,
    queryFn: async () => {
      if (!client) {
        throw new Error("Agent execution client not available");
      }
      if (!sessionId) {
        throw new Error("Session ID not available - user ID is required");
      }
      const previousData =
        queryClient.getQueryData<SessionMessagesResponse>(messagesQueryKey);
      // Messages are ordered newest-first, so [0] is the most recent.
      const lastMessageId = previousData?.messages?.[0]?.id;

      try {
        const response =
          await client.listSessionMessagesCopilotsManagementApiV0SessionsSessionIdMessagesGet(
            { sessionId },
          );

        // Invalidate hints if agent used any tool
        if (hasNewToolMessages(response.data.messages ?? [], lastMessageId)) {
          queryClient.invalidateQueries({
            queryKey: caseHintsKeys.get(wsId, caseId),
          });
        }

        return response.data;
      } catch (error) {
        // BE returns 404 when no session exists yet — treat as empty conversation.
        if (
          !previousData &&
          isAxiosError(error) &&
          error.response?.status === 404
        ) {
          return { session: undefined, messages: [] };
        }
        throw error;
      }
    },
    enabled: !!client && !!sessionId,
    refetchInterval: (query) => {
      if (query.state.status === "error") return false;
      // Pause polling while invoke is in-flight to prevent
      // overwriting optimistic cache data.
      if (invokeMutation.isPending) return false;
      const sessionStatus = query.state.data?.session?.status;
      if (!sessionStatus || TERMINAL_STATUSES.has(sessionStatus)) return false;
      return 1000;
    },
    select: (data) => ({
      session: data.session,
      messages: extractMessages(data.messages ?? []),
    }),
  });

  const session = messagesQuery.data?.session;
  const sessionStatus = session?.status;
  const isSessionTerminal =
    sessionStatus != null && TERMINAL_STATUSES.has(sessionStatus);

  const messages = messagesQuery.data?.messages ?? [];

  const sendMessage = useCallback(
    (text: string) => {
      if (invokeMutation.isPending) return;
      if (!agentId) {
        toastActions.failure({
          title: "Copilot not available",
          description: "No copilot agent is configured for this case template.",
        });
        return;
      }

      invokeMutation.mutate(text);
    },
    [agentId, invokeMutation],
  );

  // Active when: mutation in-flight, session is running, or user sent a
  // message and we haven't received a terminal status yet.
  const isActive =
    invokeMutation.isPending ||
    ((hasStarted || sessionStatus != null) && !isSessionTerminal);

  const failureMessage =
    sessionStatus === "failed"
      ? session?.termination_reason
        ? `Something went wrong: ${formatTerminationReason(session.termination_reason)}`
        : "Something went wrong"
      : null;

  return {
    messages,
    isLoading: messagesQuery.isLoading,
    isActive,
    failureMessage,
    sendMessage,
  };
};
